import React, { useEffect, useState } from "react"
import { Spin, Form as AntForm } from "antd"
import PropTypes from "prop-types"

import api from "@api"
import Form from "@components/Form"
import Modal from "@components/Page/Modal"
import Markdown from "@components/Markdown"
import { CustomerShape } from "@shapes"
import { useAppContext } from "@components/AppContext"

import useInputs from "./helpers/useInputs"
import useBeneficialOwnersStore from "./helpers/useBeneficialOwnersStore"

const certifyMyCustomerBeneficialOwnershipOperation = api.get("transactions/CertifyMyCustomerBeneficialOwnership")

const LABEL_TITLE = "Add Owners"
const LABEL_HEADER = `Due to regulatory requirements, we need to identify
anyone who owns 25% or more of **[ORGANIZATION]**. This can include venture
capitalists, investors, managing directors, individual owners, etc.`


const CertifyBeneficialOwnershipModal = ({
  modal,
  customer,
  onSuccess,
  isModalOpen,
}) => {
  const [ form ] = AntForm.useForm()

  const { authenticatedRequest } = useAppContext()

  const [ isOpen, setIsOpen ] = useState(isModalOpen)
  const [ isLoading, setIsLoading ] = useState(false)

  const { id: customerId } = customer

  const { beneficialOwners, indexBeneficialOwners } = useBeneficialOwnersStore(customerId)
  const schema = useInputs(customer, form, indexBeneficialOwners, beneficialOwners)

  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    if (!!beneficialOwners) {
      return
    }

    indexBeneficialOwners()
  }, [ isModalOpen, beneficialOwners ])

  const onOpen = async() => {
    setIsLoading(true)
    await indexBeneficialOwners()
    setIsLoading(false)
  }

  const onClose = () =>
    setIsOpen(false)

  modal.current.open = () => {
    setIsOpen(true)
    onOpen()
  }

  modal.current.close = () =>
    onClose()

  const onSubmit = async parameters => {
    const hasFormError = await form.validateFields()
      .then(() => false)
      .catch(() => true)

    if (!hasFormError) {
      return parameters
    }
  }

  const formOptions = {
    form,
    schema,
    onSuccess,
    item: customer,
    request: authenticatedRequest,
    operation: certifyMyCustomerBeneficialOwnershipOperation.id,
    hasSubmitButton: false,
    shouldOptimizeUpdateMutation: false,
    onSubmit
  }

  const { name } = customer

  const header = LABEL_HEADER
    .replace('[ORGANIZATION]', name)

  return (
    <Modal
      title={LABEL_TITLE}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Markdown>
        {header}
      </Markdown>

      <Spin spinning={isLoading}>
        <Form
          {...formOptions}
        />
      </Spin>
    </Modal>
  )
}

CertifyBeneficialOwnershipModal.propTypes = {
  modal: PropTypes.shape().isRequired,
  customer: CustomerShape.isRequired,
  onSuccess: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
}

export default CertifyBeneficialOwnershipModal
